/**
 * 缓存管理 API
 * 用于权限、菜单、角色变更时清除相关用户的缓存
 */

import PermissionCacheStore from './cacheService.ts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';

function getHeaders() {
  const headers = new Headers({ Accept: 'application/json' });
  const authToken = localStorage.getItem('session') || localStorage.getItem('token');
  if (authToken) headers.set('Authorization', `Bearer ${authToken}`);
  return headers;
}

/**
 * 缓存管理接口
 */
export class CacheManager {
  /**
   * 用户权限变更 - 清除该用户的缓存
   */
  static invalidateUserCache(userId: string | number): void {
    PermissionCacheStore.clearUserCache(String(userId));
    // eslint-disable-next-line no-console
    console.log(`[CacheManager] User ${userId} cache invalidated`);
  }

  /**
   * 角色权限变更 - 清除该角色所有用户的缓存
   */
  static async invalidateRoleCache(roleId: string | number): Promise<void> {
    try {
      // eslint-disable-next-line no-console
      console.log(`[CacheManager] Invalidating cache for role ${roleId}`);
      await PermissionCacheStore.clearRoleUsersCache(String(roleId));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[CacheManager] Failed to invalidate role cache:', error);
    }
  }

  /**
   * 菜单权限变更 - 清除相关用户的缓存
   */
  static async invalidateMenuCache(menuId: string | number): Promise<void> {
    try {
      // eslint-disable-next-line no-console
      console.log(`[CacheManager] Invalidating cache for menu ${menuId}`);
      await PermissionCacheStore.clearMenuRelatedUsersCache(String(menuId));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[CacheManager] Failed to invalidate menu cache:', error);
    }
  }

  /**
   * 批量清除用户缓存
   */
  static invalidateUsersCache(userIds: (string | number)[]): void {
    const ids = userIds.map(id => String(id));
    PermissionCacheStore.clearUsersCache(ids);
    // eslint-disable-next-line no-console
    console.log(`[CacheManager] Cleared cache for ${ids.length} users`);
  }

  /**
   * 清除所有缓存
   */
  static invalidateAllCache(): void {
    PermissionCacheStore.clearAllCache();
    // eslint-disable-next-line no-console
    console.log('[CacheManager] All permission caches cleared');
  }

  /**
   * 获取缓存统计
   */
  static getStats() {
    return PermissionCacheStore.getStats();
  }
}

/**
 * 监听权限变更事件（WebSocket 或事件总线）
 * 这里可以集成您的实时通知系统
 */
export class PermissionChangeListener {
  private static listeners: Map<string, Function[]> = new Map();

  /**
   * 注册事件监听器
   */
  static on(event: 'userPermissionChanged' | 'rolePermissionChanged' | 'menuPermissionChanged', callback: Function) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(callback);
  }

  /**
   * 移除事件监听器
   */
  static off(event: string, callback: Function) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * 触发事件
   */
  static emit(event: string, data: any) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(cb => cb(data));
    }
  }

  /**
   * 处理用户权限变更
   */
  static handleUserPermissionChanged(userId: string | number) {
    CacheManager.invalidateUserCache(userId);
    this.emit('userPermissionChanged', { userId });
    // eslint-disable-next-line no-console
    console.log(`[PermissionChangeListener] User ${userId} permission changed event`);
  }

  /**
   * 处理角色权限变更
   */
  static async handleRolePermissionChanged(roleId: string | number) {
    await CacheManager.invalidateRoleCache(roleId);
    this.emit('rolePermissionChanged', { roleId });
    // eslint-disable-next-line no-console
    console.log(`[PermissionChangeListener] Role ${roleId} permission changed event`);
  }

  /**
   * 处理菜单权限变更
   */
  static async handleMenuPermissionChanged(menuId: string | number) {
    await CacheManager.invalidateMenuCache(menuId);
    this.emit('menuPermissionChanged', { menuId });
    // eslint-disable-next-line no-console
    console.log(`[PermissionChangeListener] Menu ${menuId} permission changed event`);
  }

  /**
   * 处理批量权限变更
   */
  static async handleBatchPermissionChanged(userIds?: (string | number)[], roleIds?: (string | number)[], menuIds?: (string | number)[]) {
    if (userIds && userIds.length > 0) {
      CacheManager.invalidateUsersCache(userIds);
    }

    if (roleIds && roleIds.length > 0) {
      for (const roleId of roleIds) {
        await CacheManager.invalidateRoleCache(roleId);
      }
    }

    if (menuIds && menuIds.length > 0) {
      for (const menuId of menuIds) {
        await CacheManager.invalidateMenuCache(menuId);
      }
    }

    // eslint-disable-next-line no-console
    console.log('[PermissionChangeListener] Batch permission changed');
  }
}

/**
 * 导出全局缓存管理对象供浏览器 console 调试使用
 */
if (typeof window !== 'undefined') {
  (window as any).__cacheDebug = {
    invalidateUser: (userId: string | number) => CacheManager.invalidateUserCache(userId),
    invalidateRole: (roleId: string | number) => CacheManager.invalidateRoleCache(roleId),
    invalidateMenu: (menuId: string | number) => CacheManager.invalidateMenuCache(menuId),
    invalidateAll: () => CacheManager.invalidateAllCache(),
    getStats: () => CacheManager.getStats(),
    handleUserChanged: (userId: string | number) => PermissionChangeListener.handleUserPermissionChanged(userId),
    handleRoleChanged: (roleId: string | number) => PermissionChangeListener.handleRolePermissionChanged(roleId),
    handleMenuChanged: (menuId: string | number) => PermissionChangeListener.handleMenuPermissionChanged(menuId),
  };
}

export default CacheManager;
